import React, { useState, useEffect } from 'react';
import { crmApi } from '../services/crmApi';
import {
  Archive, Users, CheckCircle, XCircle, Mail, Phone, FileText,
  MessageSquare, Award, TrendingUp, ChevronDown, ChevronUp,
} from 'lucide-react';

type CrmRow = {
  service_type: string; year: number; total: number; archived: number;
  active: number; success: number; rejected: number; cancelled: number;
  has_email: number; has_phone: number; has_notes: number;
  with_comments: number; with_documents: number;
  total_amount?: number; success_amount?: number;
};
type TrainingRow = {
  year: number; sessions: number; archived_sessions: number;
  enrollments: number; certificates: number; unique_participants: number;
};
type TurnusyRow = {
  year: number; trips: number; archived_trips: number;
  bookings: number; unique_clients: number;
};
type WycieczkiRow = {
  year: number; trips: number; archived_trips: number; bookings: number;
};

interface Props {
  productCode: string;
  selectedYear: number;
}

const SERVICE_LABELS: Record<string, string> = {
  pup_grant: 'Dotacje PUP',
  kfs: 'KFS',
  dev_grant: 'Dotacje rozwojowe',
  szkolenia: 'Szkolenia',
  turnusy: 'Turnusy',
  wycieczki: 'Wycieczki',
};

function pct(n: number, total: number): string {
  if (!total) return '—';
  return `${Math.round((n / total) * 100)}%`;
}

function StatCard({ icon: Icon, label, value, sub, color }: {
  icon: any; label: string; value: string | number; sub?: string; color: string;
}) {
  return (
    <div className={`flex items-center gap-3 bg-white border rounded-lg px-4 py-3 ${color}`}>
      <Icon className="w-5 h-5 flex-shrink-0 opacity-70" />
      <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-lg font-semibold">{value}</div>
        {sub && <div className="text-xs text-gray-400">{sub}</div>}
      </div>
    </div>
  );
}

function DataBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const w = total ? Math.max(4, (value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-24 text-gray-500 text-right">{label}</span>
      <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden">
        <div className={`h-full ${color} rounded transition-all`} style={{ width: `${w}%` }} />
      </div>
      <span className="w-16 text-gray-600">{value}/{total}</span>
    </div>
  );
}

export function ArchiveSummary({ productCode, selectedYear }: Props) {
  const [data, setData] = useState<{
    crm: CrmRow[]; training: TrainingRow[]; turnusy: TurnusyRow[]; wycieczki: WycieczkiRow[];
  } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    crmApi.getArchiveStats(productCode === 'all' ? undefined : productCode).then(d => {
      if (!cancelled) { setData(d); setLoading(false); }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productCode]);

  if (loading) return <div className="text-sm text-gray-400 py-2">Ładowanie statystyk archiwum...</div>;
  if (!data) return null;

  const currentYear = new Date().getFullYear();
  const isArchiveView = selectedYear < currentYear || selectedYear === 0;

  // Filter data for selected year (0 = all)
  const crmRows = selectedYear === 0
    ? data.crm
    : data.crm.filter(r => r.year === selectedYear);
  const trainingRows = selectedYear === 0
    ? data.training
    : data.training.filter(r => r.year === selectedYear);
  const turnusyRows = selectedYear === 0
    ? data.turnusy
    : data.turnusy.filter(r => r.year === selectedYear);
  const wycieczekRows = selectedYear === 0
    ? data.wycieczki
    : data.wycieczki.filter(r => r.year === selectedYear);

  // Aggregate CRM stats
  const crmAgg = crmRows.reduce((acc, r) => ({
    total: acc.total + r.total,
    success: acc.success + r.success,
    rejected: acc.rejected + r.rejected,
    cancelled: acc.cancelled + r.cancelled,
    hasEmail: acc.hasEmail + r.has_email,
    hasPhone: acc.hasPhone + r.has_phone,
    hasNotes: acc.hasNotes + r.has_notes,
    withComments: acc.withComments + r.with_comments,
    withDocs: acc.withDocs + r.with_documents,
    totalAmount: acc.totalAmount + (r.total_amount || 0),
    successAmount: acc.successAmount + (r.success_amount || 0),
  }), { total: 0, success: 0, rejected: 0, cancelled: 0, hasEmail: 0, hasPhone: 0, hasNotes: 0, withComments: 0, withDocs: 0, totalAmount: 0, successAmount: 0 });

  const traAgg = trainingRows.reduce((acc, r) => ({
    sessions: acc.sessions + r.sessions,
    enrollments: acc.enrollments + r.enrollments,
    certificates: acc.certificates + r.certificates,
    participants: acc.participants + r.unique_participants,
  }), { sessions: 0, enrollments: 0, certificates: 0, participants: 0 });

  const turAgg = turnusyRows.reduce((acc, r) => ({
    trips: acc.trips + r.trips,
    bookings: acc.bookings + r.bookings,
    clients: acc.clients + r.unique_clients,
  }), { trips: 0, bookings: 0, clients: 0 });

  const wycAgg = wycieczekRows.reduce((acc, r) => ({
    trips: acc.trips + r.trips,
    bookings: acc.bookings + r.bookings,
  }), { trips: 0, bookings: 0 });

  const hasData = crmAgg.total > 0 || traAgg.sessions > 0 || turAgg.trips > 0 || wycAgg.trips > 0;
  if (!hasData) return null;

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <Archive className="w-4 h-4" />
        <span>Statystyki archiwum {selectedYear > 0 ? selectedYear : '(wszystkie lata)'}</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-4">
          {/* CRM Applications */}
          {crmAgg.total > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">Wnioski CRM</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <StatCard icon={Users} label="Razem" value={crmAgg.total} color="border-gray-200" />
                <StatCard icon={CheckCircle} label="Sukces" value={crmAgg.success}
                  sub={pct(crmAgg.success, crmAgg.total)} color="border-green-200" />
                <StatCard icon={XCircle} label="Odrzucone" value={crmAgg.rejected}
                  sub={pct(crmAgg.rejected, crmAgg.total)} color="border-red-200" />
                <StatCard icon={TrendingUp} label="Skuteczność"
                  value={pct(crmAgg.success, crmAgg.success + crmAgg.rejected)}
                  sub={`${crmAgg.success}/${crmAgg.success + crmAgg.rejected} zakoncz.`}
                  color="border-blue-200" />
              </div>
              <div className="mt-3 space-y-1">
                <DataBar label="Email" value={crmAgg.hasEmail} total={crmAgg.total} color="bg-blue-400" />
                <DataBar label="Telefon" value={crmAgg.hasPhone} total={crmAgg.total} color="bg-green-400" />
                <DataBar label="Notatki" value={crmAgg.hasNotes} total={crmAgg.total} color="bg-amber-400" />
                <DataBar label="Komentarze" value={crmAgg.withComments} total={crmAgg.total} color="bg-purple-400" />
                <DataBar label="Dokumenty" value={crmAgg.withDocs} total={crmAgg.total} color="bg-teal-400" />
              </div>
              {(crmAgg.totalAmount > 0 || crmAgg.successAmount > 0) && (
                <div className="mt-3 pt-2 border-t border-gray-100 flex flex-wrap gap-4 text-sm">
                  {crmAgg.totalAmount > 0 && (
                    <span className="text-gray-600">Suma wnioskowana: <strong>{crmAgg.totalAmount.toLocaleString('pl-PL')} zł</strong></span>
                  )}
                  {crmAgg.successAmount > 0 && (
                    <span className="text-green-700">Suma przyznana: <strong>{crmAgg.successAmount.toLocaleString('pl-PL')} zł</strong></span>
                  )}
                </div>
              )}

              {/* Per service type breakdown */}
              {crmRows.length > 1 && (
                <div className="mt-3">
                  <table className="w-full text-xs text-gray-600">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Produkt</th>
                        <th className="text-left py-1">Rok</th>
                        <th className="text-right py-1">Razem</th>
                        <th className="text-right py-1">Sukces</th>
                        <th className="text-right py-1">Odrz.</th>
                        <th className="text-right py-1">Email%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crmRows.map((r, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-1">{SERVICE_LABELS[r.service_type] || r.service_type}</td>
                          <td className="py-1">{r.year}</td>
                          <td className="text-right">{r.total}</td>
                          <td className="text-right text-green-600">{r.success}</td>
                          <td className="text-right text-red-600">{r.rejected}</td>
                          <td className="text-right">{pct(r.has_email, r.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Training */}
          {traAgg.sessions > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">Szkolenia</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <StatCard icon={FileText} label="Sesje" value={traAgg.sessions} color="border-gray-200" />
                <StatCard icon={Users} label="Uczestnicy" value={traAgg.participants} color="border-blue-200" />
                <StatCard icon={Award} label="Certyfikaty" value={traAgg.certificates}
                  sub={pct(traAgg.certificates, traAgg.enrollments)} color="border-green-200" />
                <StatCard icon={MessageSquare} label="Zapisy" value={traAgg.enrollments} color="border-amber-200" />
              </div>
            </div>
          )}

          {/* Turnusy */}
          {turAgg.trips > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">Turnusy</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <StatCard icon={FileText} label="Turnusy" value={turAgg.trips} color="border-gray-200" />
                <StatCard icon={Users} label="Rezerwacje" value={turAgg.bookings} color="border-blue-200" />
                <StatCard icon={Users} label="Klienci" value={turAgg.clients} color="border-green-200" />
              </div>
            </div>
          )}

          {/* Wycieczki */}
          {wycAgg.trips > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">Wycieczki</h4>
              <div className="grid grid-cols-2 gap-2">
                <StatCard icon={FileText} label="Wycieczki" value={wycAgg.trips} color="border-gray-200" />
                <StatCard icon={Users} label="Rezerwacje" value={wycAgg.bookings} color="border-blue-200" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
